import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Lightbulb, Plus,
  Shield, Mail, Laptop, Lock, Network, Wifi, Bug, Activity,
  Workflow, ScanLine, Layers, ShieldAlert, Cloud, Boxes,
} from "lucide-react";

const ICONS = {
  Shield, Mail, Laptop, Lock, Network, Wifi, Bug, Activity,
  Workflow, ScanLine, Layers, ShieldAlert, Cloud,
};

const PRIORITY_LABELS = {
  1: { label: "Indispensable", color: "bg-[#D42B1E] text-white" },
  2: { label: "Recommandé", color: "bg-amber-500 text-white" },
  3: { label: "À considérer", color: "bg-zinc-500 text-white" },
};

/**
 * BomRecommendations — surface les solutions complémentaires à ajouter
 * basées sur l'analyse du BOM (catégorie + use case + utilisateurs +
 * niveau sécurité). Chaque carte montre la raison métier et un bouton
 * « Ajouter » qui appelle `onAddAddon(solution_id)` côté parent.
 */
export default function BomRecommendations({
  recommendations,
  onAddAddon,
  securityLevel,
  licenseYears,
  bom = [],
  pendingAddons = [],
  embedded = false,
}) {
  const [adding, setAdding] = useState(null); // solution_id en cours d'ajout

  // Filter out recommendations that are already represented either in the
  // generated BOM or in the user's pending addons cart, so the UI doesn't
  // keep nagging to add an indispensable line that was just clicked.
  const bomSkus = new Set((bom || []).map((r) => (r?.sku || "").toUpperCase()).filter(Boolean));
  const pendingSkus = new Set((pendingAddons || []).map((a) => (a?.sku || "").toUpperCase()).filter(Boolean));
  const pendingSolutionIds = new Set(
    (pendingAddons || []).map((a) => a?.solution_id).filter(Boolean)
  );

  const visible = (recommendations || []).filter((rec) => {
    const sku = (rec.direct_sku || "").toUpperCase();
    if (sku && (bomSkus.has(sku) || pendingSkus.has(sku))) return false;
    if (rec.solution_id && pendingSolutionIds.has(rec.solution_id)) return false;
    return true;
  });

  if (!visible || visible.length === 0) return null;

  const handleAdd = async (rec) => {
    if (!onAddAddon) return;
    setAdding(rec.solution_id);
    try {
      // Backend may already attach a specific SKU directly (e.g. FortiCare/
      // FortiGuard subscription tied to the FG-VM tier present in the BOM,
      // or a use-case-driven auto add-on like FortiManager/FortiAnalyzer VM).
      // In that case skip the per-solution SKU lookup.
      if (rec.direct_sku) {
        onAddAddon({
          solution_id: rec.solution_id,
          sku: rec.direct_sku,
          name: rec.direct_name || rec.direct_sku,
          price: rec.direct_price || 0,
          quantity: Math.max(1, parseInt(rec.direct_quantity || 1, 10) || 1),
        });
        toast.success(`${rec.name} ajouté — cliquez sur Générer pour rafraîchir le BOM.`);
        return;
      }
      // Fetch the first SKU for this solution — prefer bundle matching the
      // current security_level + license_years (so FortiCare + license are included).
      const r = await api.get(`/solutions/${rec.solution_id}/skus`, {
        params: {
          limit: 1,
          security_level: securityLevel || "",
          license_years: licenseYears || 0,
        },
      });
      const item = r.data?.items?.[0];
      if (!item) {
        toast.error("Aucun SKU disponible");
        return;
      }
      onAddAddon({
        solution_id: rec.solution_id,
        sku: item.sku,
        name: item.name,
        price: item.price,
        quantity: 1,
      });
      toast.success(`${rec.name} ajouté — cliquez sur Générer pour rafraîchir le BOM.`);
    } catch {
      toast.error("Échec de l'ajout");
    } finally {
      setAdding(null);
    }
  };

  if (embedded) {
    // Inside the unified Recommendations panel: skip the outer card + header
    // and just render the divided list of recommendation cards.
    return (
      <div className="divide-y divide-zinc-100" data-testid="bom-recommendations-embedded">
        {visible.map((rec) => {
          const Icon = ICONS[rec.icon] || Boxes;
          const pr = PRIORITY_LABELS[rec.priority] || PRIORITY_LABELS[3];
          const isAdding = adding === rec.solution_id;
          const isAutoAddon = rec.kind === "addon_auto";
          return (
            <div
              key={rec.solution_id}
              className={`px-5 py-4 flex items-start gap-4 transition-colors ${
                isAutoAddon
                  ? "bg-[#D42B1E]/5 border-l-4 border-[#D42B1E] hover:bg-[#D42B1E]/10"
                  : "hover:bg-zinc-50/40"
              }`}
              data-testid={`bom-reco-${rec.solution_id}`}
            >
              <div className={`shrink-0 h-10 w-10 rounded-sm flex items-center justify-center ${
                isAutoAddon ? "bg-[#D42B1E] text-white animate-pulse-slow" : "bg-zinc-100"
              }`}>
                <Icon className={`h-4 w-4 ${isAutoAddon ? "text-white" : "text-zinc-700"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {isAutoAddon && (
                    <span
                      className="text-[9px] px-2 py-0.5 rounded-sm font-bold uppercase tracking-[0.18em] bg-[#D42B1E] text-white shadow-sm"
                      data-testid={`bom-reco-addon-badge-${rec.solution_id}`}
                    >
                      Add-on
                    </span>
                  )}
                  <span className="text-sm font-semibold text-zinc-900">{rec.name}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{rec.category}</span>
                  <span
                    className={`text-[9px] px-2 py-0.5 rounded-sm font-medium uppercase tracking-wide ${pr.color}`}
                    data-testid={`bom-reco-priority-${rec.solution_id}`}
                  >
                    {pr.label}
                  </span>
                  {isAutoAddon && (
                    <span
                      className="text-[9px] px-2 py-0.5 rounded-sm font-bold uppercase tracking-wide bg-amber-400 text-amber-900 border border-amber-500"
                      data-testid={`bom-reco-todo-${rec.solution_id}`}
                    >
                      ⚠ À ajouter à la BOM
                    </span>
                  )}
                </div>
                {isAutoAddon && rec.direct_sku && (
                  <p className="mt-1 text-[11px] font-mono text-[#991D14]">
                    SKU : {rec.direct_sku}
                    {rec.direct_quantity > 1 ? ` · ×${rec.direct_quantity}` : ""}
                    {rec.direct_price ? ` · ${new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(rec.direct_price)}` : ""}
                  </p>
                )}
                <p className={`mt-1 text-sm leading-relaxed ${isAutoAddon ? "text-zinc-800 font-medium" : "text-zinc-600"}`}>
                  {rec.reason}
                </p>
              </div>
              {onAddAddon && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleAdd(rec)}
                  disabled={isAdding}
                  variant={isAutoAddon ? "default" : "outline"}
                  className={`rounded-sm shrink-0 mt-0.5 ${
                    isAutoAddon
                      ? "bg-[#D42B1E] hover:bg-[#B32218] text-white border-[#D42B1E]"
                      : "border-zinc-300"
                  }`}
                  data-testid={`bom-reco-add-${rec.solution_id}`}
                >
                  <Plus className="h-3 w-3 mr-1.5" />
                  {isAdding ? "Ajout…" : isAutoAddon ? "Ajouter à la BOM" : "Ajouter"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 bg-white rounded-sm overflow-hidden" data-testid="bom-recommendations-card">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Lightbulb className="h-3.5 w-3.5 text-[#D42B1E]" />
        <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-zinc-500">
          Recommandations complémentaires
        </h3>
        <span className="text-[11px] text-zinc-400 font-mono ml-auto">
          {visible.length} solution{visible.length > 1 ? "s" : ""} suggérée{visible.length > 1 ? "s" : ""}
        </span>
      </div>

      <div className="divide-y divide-zinc-100">
        {visible.map((rec) => {
          const Icon = ICONS[rec.icon] || Boxes;
          const pr = PRIORITY_LABELS[rec.priority] || PRIORITY_LABELS[3];
          const isAdding = adding === rec.solution_id;
          return (
            <div
              key={rec.solution_id}
              className="px-5 py-4 flex items-start gap-4 hover:bg-zinc-50/40 transition-colors"
              data-testid={`bom-reco-${rec.solution_id}`}
            >
              <div className="shrink-0 h-10 w-10 rounded-sm bg-zinc-100 flex items-center justify-center">
                <Icon className="h-4 w-4 text-zinc-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-zinc-900">{rec.name}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{rec.category}</span>
                  <span
                    className={`text-[9px] px-2 py-0.5 rounded-sm font-medium uppercase tracking-wide ${pr.color}`}
                    data-testid={`bom-reco-priority-${rec.solution_id}`}
                  >
                    {pr.label}
                  </span>
                </div>
                <p className="mt-1 text-sm text-zinc-600 leading-relaxed">{rec.reason}</p>
              </div>
              {onAddAddon && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleAdd(rec)}
                  disabled={isAdding}
                  className="rounded-sm border-zinc-300 shrink-0 mt-0.5"
                  data-testid={`bom-reco-add-${rec.solution_id}`}
                >
                  <Plus className="h-3 w-3 mr-1.5" />
                  {isAdding ? "Ajout…" : "Ajouter"}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
